import { useState } from 'react'
import { z } from 'zod'
import { type Control, type FieldPath, useWatch } from 'react-hook-form'
import { type TFunction, useTranslation } from '@clofo/i18n'
import { FormInput } from '@clofo/ui/controlled-field'
import { IceIcon } from '@clofo/ui/ice-icon'
import { cn } from '@clofo/ui/utils'

/**
 * Password + confirmation pair with a live requirements indicator, shared by the reset-password
 * form and the invited-user registration form. Ported from the legacy cf-common
 * c-forms/password-fields component.
 */

export interface PasswordFieldValues {
  newPassword1: string
  newPassword2: string
}

// Mirrors the legacy strongPassword validator: at least one lowercase, uppercase, digit, and
// non-alphanumeric character. Length bounds are enforced separately for distinct error messages.
const STRONG_PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).*$/

/**
 * Zod shape for the two password fields. Spread into the consuming form's z.object, then enforce
 * the match at the object level so the error lands on the confirmation field:
 *
 *   z.object({ ...passwordFieldsShape(t) }).refine(v => v.newPassword1 === v.newPassword2, {
 *     error: t('login.passwordFields.mismatch'),
 *     path: ['newPassword2'],
 *   })
 */
export const passwordFieldsShape = (t: TFunction) => ({
  newPassword1: z
    .string()
    .min(1, { error: t('login.passwordFields.provideValue') })
    .min(8, { error: t('login.passwordFields.minLength') })
    .max(30, { error: t('login.passwordFields.maxLength') })
    .regex(STRONG_PASSWORD_REGEX, { error: t('login.passwordFields.strongPassword') }),
  newPassword2: z.string().min(1, { error: t('login.passwordFields.provideValue') }),
})

const requirements = [
  { key: 'lower', isMet: (password: string) => /[a-z]/.test(password) },
  { key: 'upper', isMet: (password: string) => /[A-Z]/.test(password) },
  { key: 'number', isMet: (password: string) => /\d/.test(password) },
  { key: 'symbol', isMet: (password: string) => /[\W_]/.test(password) },
  { key: 'length', isMet: (password: string) => password.length >= 8 },
] as const

/**
 * Requirements flyout revealed while the password input is focused, positioned to the right of
 * the field with a connector line — same pattern as the account-settings password page in
 * overview. It stays in the DOM (opacity transition only) so the aria-live region keeps
 * announcing requirement changes.
 */
const PasswordRequirementsFlyout = ({
  password,
  isVisible,
}: {
  password: string
  isVisible: boolean
}) => {
  const { t } = useTranslation()

  return (
    <div
      className={cn(
        'absolute left-full top-12 -translate-y-1/2 ml-11 pointer-events-none',
        'opacity-0 transition-opacity duration-300 ease-in-out',
        isVisible && 'opacity-100',
      )}
      aria-live='polite'
    >
      <div className='relative'>
        {/* connector line from the input to the list */}
        <div className='absolute -left-11 top-1/2 w-11.25 h-px bg-border' aria-hidden='true' />
        <ul className='list-none m-0 py-1 pl-3 border-l border-border'>
          {requirements.map(({ key, isMet }) => {
            const met = isMet(password)
            return (
              <li
                key={key}
                className={cn(
                  'flex items-center gap-1.5 mb-1.5 last:mb-0 text-xs whitespace-nowrap',
                  met ? 'text-success' : 'text-foreground/80',
                )}
              >
                <IceIcon
                  name={met ? 'checkmark' : 'close-delete-x'}
                  className={cn('text-sm', met ? 'text-success' : 'text-destructive')}
                />
                {t(`login.passwordFields.requirements.${key}`)}
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

export const PasswordFields = <T extends PasswordFieldValues>({
  control,
}: {
  control: Control<T>
}) => {
  const { t } = useTranslation()
  const [isPasswordFocused, setIsPasswordFocused] = useState(false)
  const newPassword1 =
    (useWatch({ control, name: 'newPassword1' as FieldPath<T> }) as string | undefined) ?? ''

  return (
    <>
      <div className='relative mb-6 max-w-md'>
        <FormInput
          control={control}
          name={'newPassword1' as FieldPath<T>}
          type='password'
          label={t('login.passwordFields.newPassword')}
          isRequired
          autoComplete='new-password'
          description={t('login.passwordFields.description')}
          className={{ input: 'h-12' }}
          onFocus={() => setIsPasswordFocused(true)}
          onBlur={() => setIsPasswordFocused(false)}
        />
        <PasswordRequirementsFlyout password={newPassword1} isVisible={isPasswordFocused} />
      </div>

      <FormInput
        control={control}
        name={'newPassword2' as FieldPath<T>}
        type='password'
        label={t('login.passwordFields.confirmPassword')}
        isRequired
        autoComplete='new-password'
        className={{ field: 'max-w-md', input: 'h-12' }}
      />
    </>
  )
}
